import React, {Component} from 'react';
import {connect} from 'react-redux';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Input from '@material-ui/core/Input';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Preloader from '../../../components/preloader';
import Button from '../../../components/button';
import RiTableView from './RiTableView';
import RiDiagram from './RiDiagram';
import RiImpactPanel from './RiImpactPanel';
import {loadGraph, loadColumns, refreshRelationships, loadSchemasForInstance} from '../dependency-analysis.actions';

function hasStaleness(graph) {
  if (!graph || !graph.meta) return false;
  return !!(graph.meta.hasViews && !graph.meta.hasViewDepEdges);
}

function buildCsvRows(graph) {
  if (!graph || !graph.edges) return [];
  const header = ['Direction', 'Parent Object', 'Parent Column', 'Child Object', 'Child Column',
    'Type', 'Constraint', 'Cascade', 'Cross-schema', 'FK Index'];
  const rows = (graph.edges || []).map((e) => [
    e.isUpstream ? 'Upstream' : e.isDownstream ? 'Downstream' : '',
    e.parentObjectName || '',
    e.parentColumnName || '',
    e.childObjectName || '',
    e.childColumnName || '',
    e.relType || 'FK',
    e.constraintName || '',
    e.cascadeDelete || '',
    e.crossSchema ? 'Yes' : 'No',
    e.fkIndexPresent === true ? 'Yes' : e.fkIndexPresent === false ? 'No' : 'Unknown',
  ]);
  return [header, ...rows];
}

class ReferentialIntegrityTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      instanceId: '',
      schemaId: '',
      view: 'table',
      columnDetail: false,
      filterObject: '',
      filterType: 'all',
      filterDirection: 'both',
      filterRelType: 'all',
      filterCrossSchema: false,
      search: '',
      impactOpen: false,
      impactObjectId: null,
    };
    this.handleInstanceChange = this.handleInstanceChange.bind(this);
    this.handleSchemaChange = this.handleSchemaChange.bind(this);
    this.handleRefresh = this.handleRefresh.bind(this);
    this.handleExportCsv = this.handleExportCsv.bind(this);
    this.handleColumnDetailToggle = this.handleColumnDetailToggle.bind(this);
    this.handleViewChange = this.handleViewChange.bind(this);
    this.handleOpenImpact = this.handleOpenImpact.bind(this);
    this.handleExpandMore = this.handleExpandMore.bind(this);
  }

  fetchGraph(overrides) {
    const merged = {...this.state, ...overrides};
    const {schemaId, filterObject, filterDirection, columnDetail} = merged;
    if (!schemaId) return;
    this.props.loadGraph({
      schemaId,
      objectId: filterObject || undefined,
      direction: filterDirection !== 'both' ? filterDirection : undefined,
      includeColumns: columnDetail || undefined,
    });
  }

  handleInstanceChange(e) {
    const instanceId = e.target.value;
    this.setState({instanceId, schemaId: ''});
    this.props.loadSchemasForInstance(instanceId);
  }

  handleSchemaChange(e) {
    const schemaId = e.target.value;
    this.setState({schemaId}, () => this.fetchGraph({}));
  }

  handleRefresh() {
    const {schemaId} = this.state;
    if (!schemaId) return;
    this.props.refreshRelationships(schemaId).then(() => this.fetchGraph({}));
  }

  handleExportCsv() {
    const {graph} = this.props;
    const rows = buildCsvRows(graph);
    if (rows.length === 0) return;
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], {type: 'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'referential-integrity.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  handleColumnDetailToggle() {
    this.setState((prev) => {
      const columnDetail = !prev.columnDetail;
      this.fetchGraph({columnDetail});
      return {columnDetail};
    });
  }

  handleViewChange(view) {
    this.setState({view});
  }

  handleOpenImpact(objectId) {
    this.setState({impactOpen: true, impactObjectId: objectId || null});
  }

  handleExpandMore(objectId) {
    this.props.loadColumns([objectId]);
  }

  render() {
    const {instances, ddSchemas, graph, columnsByObject, isLoading, auth} = this.props;
    const {
      instanceId, schemaId, view, columnDetail,
      filterObject, filterType, filterDirection, filterRelType, filterCrossSchema,
      search, impactOpen, impactObjectId,
    } = this.state;

    const canEdit = auth && auth.hasPermission('dependency_analysis', 'can_edit');
    const graphLoading = isLoading.includes('loadGraph');
    const refreshing = isLoading.includes('refresh');
    const stale = hasStaleness(graph);

    const graphNodeOptions = (graph && graph.nodes) ? graph.nodes : [];

    const filters = {objectId: filterObject, type: filterType, direction: filterDirection, relType: filterRelType, crossSchema: filterCrossSchema};

    return (
      <div>
        {/* Controls: scope pickers (top row) + filters (divided row) in one panel */}
        <div className="ri-controls-panel">
          <div className="ri-controls-row ri-controls-scope">
            <div className="ri-field">
              <span className="ri-field-label">Instance</span>
              <Select
                value={instanceId}
                onChange={this.handleInstanceChange}
                displayEmpty
                inputProps={{'data-testid': 'ri-instance-select'}}
              >
                <MenuItem value=""><em>Select instance…</em></MenuItem>
                {(instances || []).map((i) => {
                  const id = i.instanceId || i.instance_id || i.id;
                  const name = i.instanceName || i.instance_name || i.name || i.host || id;
                  return <MenuItem key={id} value={id}>{name}</MenuItem>;
                })}
              </Select>
            </div>

            <div className="ri-field">
              <span className="ri-field-label">DD Schema</span>
              <Select
                value={schemaId}
                onChange={this.handleSchemaChange}
                displayEmpty
                disabled={!instanceId}
                inputProps={{'data-testid': 'ri-schema-select'}}
              >
                <MenuItem value="">
                  <em>{instanceId ? 'Select schema…' : 'Select an instance first'}</em>
                </MenuItem>
                {(ddSchemas || []).map((s) => {
                  const id = s.schemaId || s.schema_id;
                  return <MenuItem key={id} value={id}>{s.schemaName || s.schema_name}</MenuItem>;
                })}
              </Select>
            </div>
          </div>

          <div className="ri-controls-row ri-controls-filters">
            <div className="ri-field">
              <span className="ri-field-label">Object</span>
              <Select
                value={filterObject}
                onChange={(e) => {
                  const val = e.target.value;
                  this.setState({filterObject: val}, () => this.fetchGraph({}));
                }}
                displayEmpty
                inputProps={{'data-testid': 'ri-filter-object'}}
              >
                <MenuItem value="">All</MenuItem>
                {graphNodeOptions.map((n) => {
                  const id = n.objectId || n.id;
                  return <MenuItem key={id} value={id}>{n.objectName || n.label}</MenuItem>;
                })}
              </Select>
            </div>

            <div className="ri-field">
              <span className="ri-field-label">Type</span>
              <Select
                value={filterType}
                onChange={(e) => this.setState({filterType: e.target.value})}
                inputProps={{'data-testid': 'ri-filter-type'}}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="table">Table</MenuItem>
                <MenuItem value="view">View</MenuItem>
              </Select>
            </div>

            <div className="ri-field">
              <span className="ri-field-label">Direction</span>
              <Select
                value={filterDirection}
                onChange={(e) => {
                  const val = e.target.value;
                  this.setState({filterDirection: val}, () => this.fetchGraph({}));
                }}
                inputProps={{'data-testid': 'ri-filter-direction'}}
              >
                <MenuItem value="both">Both</MenuItem>
                <MenuItem value="upstream">Upstream</MenuItem>
                <MenuItem value="downstream">Downstream</MenuItem>
              </Select>
            </div>

            <div className="ri-field">
              <span className="ri-field-label">Rel. type</span>
              <Select
                value={filterRelType}
                onChange={(e) => this.setState({filterRelType: e.target.value})}
                inputProps={{'data-testid': 'ri-filter-reltype'}}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="FK">FK</MenuItem>
                <MenuItem value="VIEW_DEP">VIEW DEP</MenuItem>
              </Select>
            </div>

            <div className="ri-crossschema-check">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterCrossSchema}
                    onChange={(e) => this.setState({filterCrossSchema: e.target.checked})}
                    inputProps={{'data-testid': 'ri-filter-crossschema'}}
                  />
                }
                label="Cross-schema only"
              />
            </div>

            <div className="ri-field ri-field-search">
              <span className="ri-field-label">Search</span>
              <Input
                fullWidth
                value={search}
                placeholder="Search objects…"
                onChange={(e) => this.setState({search: e.target.value})}
                inputProps={{'data-testid': 'ri-search'}}
              />
            </div>
          </div>
        </div>

        {/* Staleness banner */}
        {stale && (
          <div className="ri-staleness-banner">
            Refresh recommended — schema has views but no view-dependency edges detected.
          </div>
        )}

        {/* Results bar: view toggle (left) + column-detail & primary actions (right) */}
        <div className="ri-view-bar">
          <Tabs
            value={view}
            onChange={(e, val) => this.handleViewChange(val)}
            className="ri-view-tabs"
          >
            <Tab label="Table" value="table" data-testid="ri-view-table" />
            <Tab label="Diagram" value="diagram" data-testid="ri-view-diagram" />
          </Tabs>
          <div className="ri-view-bar-right">
            <FormControlLabel
              control={
                <Checkbox
                  checked={columnDetail}
                  onChange={this.handleColumnDetailToggle}
                  inputProps={{'data-testid': 'ri-column-detail-toggle'}}
                  disabled={!graph || view !== 'diagram'}
                />
              }
              label="Column detail"
            />
            {canEdit && (
              <Button
                label={refreshing ? 'Refreshing…' : 'Refresh'}
                data-testid="ri-refresh-btn"
                disabled={!schemaId || refreshing}
                onClick={this.handleRefresh}
              />
            )}
            <Button
              label="Export CSV"
              data-testid="ri-export-csv"
              disabled={!graph}
              onClick={this.handleExportCsv}
            />
            <Button
              label="Analyze impact"
              data-testid="ri-impact-open"
              disabled={!graph}
              onClick={() => this.handleOpenImpact(null)}
            />
          </div>
        </div>

        {graphLoading && <Preloader loading />}

        {!graphLoading && view === 'table' && (
          <RiTableView filters={filters} search={search} />
        )}

        {!graphLoading && view === 'diagram' && (
          <RiDiagram
            graph={graph}
            columnsByObject={columnsByObject}
            columnDetail={columnDetail}
            filters={{type: filterType, relType: filterRelType, crossSchema: filterCrossSchema}}
            search={search}
            onOpenImpact={this.handleOpenImpact}
            onExpandMore={this.handleExpandMore}
          />
        )}

        {!graphLoading && !graph && schemaId && (
          <div style={{color: '#606060', fontStyle: 'italic', fontSize: '1.3rem', marginTop: 16}}>
            No relationship data found for this schema.
          </div>
        )}

        <RiImpactPanel
          open={impactOpen}
          initialObjectId={impactObjectId}
          schemaId={schemaId}
          onClose={() => this.setState({impactOpen: false, impactObjectId: null})}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  instances: state.dependencyAnalysis.instances,
  ddSchemas: state.dependencyAnalysis.ddSchemas,
  graph: state.dependencyAnalysis.graph,
  columnsByObject: state.dependencyAnalysis.columnsByObject,
  isLoading: state.dependencyAnalysis.isLoading,
  auth: state.auth,
});

const mapDispatchToProps = {loadGraph, loadColumns, refreshRelationships, loadSchemasForInstance};

export default connect(mapStateToProps, mapDispatchToProps)(ReferentialIntegrityTab);
